
import { useSlackConnectionState } from "./useSlackConnectionState";
import { useConnectorState } from "./useConnectorState";
import { useSlackConnector } from "./useSlackConnector";
import { useSlackSettings } from "./useSlackSettings";
import { useSlackStorage } from "./useSlackStorage";
import { useConnectionActions } from "./useConnectionActions";
import { useSlackCallbacks } from "./useSlackCallbacks";
import { useSlackData } from "./integrationData/useSlackData";
import { useSlackHandlers } from "./handlers/useSlackHandlers";

/**
 * Main hook for managing Slack integration
 * Acts as a facade for all the smaller, specialized hooks
 */
export const useSlackIntegration = (aiStaffId: string = "1") => {
  // 1. Connection state
  const connectionState = useSlackConnectionState();
  const {
    isConnected,
    setIsConnected,
    connectionError,
    setConnectionError,
    isLoading,
    setIsLoading,
    isChecking,
    setIsChecking,
    isInitialized,
    setIsInitialized
  } = connectionState;

  // 2. Connector state (credentials)
  const {
    webhookUrl,
    setWebhookUrl,
    slackToken,
    setSlackToken,
    resetCredentials
  } = useConnectorState(aiStaffId);

  // 3. Settings state and handlers
  const {
    triggerTag,
    setTriggerTag,
    isFileProcessingEnabled,
    setIsFileProcessingEnabled,
    isAutoResponsesEnabled,
    setIsAutoResponsesEnabled,
    isRoleDetectionEnabled,
    setIsRoleDetectionEnabled,
    handleTagChange: settingsHandleTagChange,
    handleFileProcessingChange: settingsHandleFileProcessingChange,
    handleAutoResponsesChange: settingsHandleAutoResponsesChange,
    handleRoleDetectionChange: settingsHandleRoleDetectionChange,
    updateSettings
  } = useSlackSettings(aiStaffId);

  // 4. Storage operations
  const {
    loadIntegration,
    saveIntegration,
    deleteIntegration,
    saveSettings
  } = useSlackStorage(aiStaffId);

  // 5. Connector operations
  const {
    testConnection,
    handleConnect: connectionHandleConnect,
    handleDisconnect: connectionHandleDisconnect,
    isTestingConnection
  } = useSlackConnector(
    aiStaffId,
    setIsConnected,
    setConnectionError,
    setIsLoading
  );

  // 6. Load and initialize data
  useSlackData(
    aiStaffId,
    loadIntegration,
    {
      setIsConnected,
      setWebhookUrl,
      setSlackToken,
      setTriggerTag,
      setIsFileProcessingEnabled,
      setIsAutoResponsesEnabled,
      setIsRoleDetectionEnabled,
      setConnectionError,
      setIsChecking,
      setIsInitialized
    }
  );

  // 7. Connection actions
  const {
    onConnect: baseOnConnect,
    onDisconnect: baseOnDisconnect
  } = useConnectionActions(
    aiStaffId,
    connectionHandleConnect,
    connectionHandleDisconnect,
    resetCredentials
  );

  // 8. Optimized handlers
  const {
    handleConnect,
    handleDisconnect
  } = useSlackHandlers(
    aiStaffId,
    {
      connectionHandleConnect,
      baseOnDisconnect
    },
    {
      webhookUrl,
      slackToken,
      triggerTag,
      isFileProcessingEnabled,
      isAutoResponsesEnabled
    }
  );

  // 9. Settings callbacks with optimized handlers
  const {
    handleTagChange,
    handleFileProcessingChange,
    handleAutoResponsesChange,
    handleRoleDetectionChange
  } = useSlackCallbacks(
    isConnected,
    {
      handleTagChange: settingsHandleTagChange,
      handleFileProcessingChange: settingsHandleFileProcessingChange,
      handleAutoResponsesChange: settingsHandleAutoResponsesChange,
      handleRoleDetectionChange: settingsHandleRoleDetectionChange
    },
    updateSettings,
    saveSettings
  );

  // Return the combined interface
  return {
    isConnected,
    connectionError,
    webhookUrl,
    setWebhookUrl,
    slackToken,
    setSlackToken,
    triggerTag, 
    isFileProcessingEnabled,
    isAutoResponsesEnabled,
    isRoleDetectionEnabled,
    isLoading,
    isChecking,
    isTestingConnection,
    handleConnect,
    handleDisconnect,
    handleTagChange,
    handleFileProcessingChange,
    handleAutoResponsesChange,
    handleRoleDetectionChange,
    testConnection
  };
};
